import React from 'react';
import FilterPopover from '../../../community/residents/components/FilterPopover.jsx';
import { convertIsoToLocalDate, convertIsoUTCToLocal } from '../../../../libs.jsx';

// create_time is an auto-generated MySQL UTC timestamp; render it in campus-local
// time via the shared converter. Shared so every tab's column def stays in sync.
const CREATED_ON_COLUMN = { key: 'create_time', label: 'Created On', sortable: true, filterable: false, type: 'datetime-utc' };

// ---------------------------------------------------------------------------
// Column definitions — exported for each tab
// ---------------------------------------------------------------------------

/** Move-in / Update Info tabs */
export const ADD_COLUMN_DEFS = [
  { key: 'resident_first_name', label: 'First Name', sortable: true, filterable: true },
  { key: 'resident_last_name', label: 'Last Name', sortable: true, filterable: true },
  { key: 'resident_nickname', label: 'Nickname', sortable: true, filterable: true },
  { key: 'area_name', label: 'Area', sortable: true, filterable: true },
  { key: 'ehr_level_of_care_id', label: 'Care Level', sortable: true, filterable: true },
  { key: 'resident_phone', label: 'Phone', sortable: true, filterable: true },
  { key: 'resident_email', label: 'Email', sortable: true, filterable: true },
  CREATED_ON_COLUMN,
];

/** Same structure for Edit tab */
export const EDIT_COLUMN_DEFS = ADD_COLUMN_DEFS;

/** Relocate tab */
export const MOVE_COLUMN_DEFS = [
  { key: 'resident_name', label: 'Full Name', sortable: true, filterable: true },
  { key: 'old_area_name', label: 'Prev Area', sortable: true, filterable: true },
  { key: 'area_name', label: 'New Area', sortable: true, filterable: true },
  CREATED_ON_COLUMN,
];

/** Move-out tab */
export const REMOVE_COLUMN_DEFS = [
  { key: 'resident_name', label: 'Full Name', sortable: true, filterable: true },
  { key: 'area_name', label: 'Area', sortable: true, filterable: true },
  CREATED_ON_COLUMN,
];

/**
 * EhrTaskTable — generic data table for EHR task approval.
 *
 * Accepts column definitions as a prop, making it reusable for all 4 tabs.
 * Rows are clickable to open the approval dialog (no kebab menu, no checkboxes).
 *
 * Rows with status === 15 are "scheduled move-in" tasks — area not yet
 * allocated. These are shown but cannot be approved until the move-in date.
 */
const EhrTaskTable = ({
  columnDefs,
  data,
  allData,
  sortColumn,
  sortOrder,
  onSort,
  filters,
  onFilterChange,
  onRowClick,
  tzOffset = 0,
}) => {
  /** Collect unique filter values from the full (unfiltered) dataset */
  const getFilterValues = (columnKey) => {
    const values = new Set();
    (allData || data).forEach((row) => {
      const val = row[columnKey];
      values.add(val != null && val !== '' ? String(val) : '(empty)');
    });
    return [...values].sort((a, b) =>
      a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
    );
  };

  const renderSortIcon = (columnKey) => {
    if (sortColumn !== columnKey) {
      return (
        <svg
          className="dp-icon-sm dp-sort-icon dp-sort-icon--neutral"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={2}
          stroke="currentColor"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M8.25 15L12 18.75 15.75 15m-7.5-6L12 5.25 15.75 9"
          />
        </svg>
      );
    }
    return sortOrder === 'asc' ? (
      <svg
        className="dp-icon-sm dp-sort-icon dp-sort-icon--active"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={2}
        stroke="currentColor"
        aria-hidden="true"
      >
        <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" />
      </svg>
    ) : (
      <svg
        className="dp-icon-sm dp-sort-icon dp-sort-icon--active"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={2}
        stroke="currentColor"
        aria-hidden="true"
      >
        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
      </svg>
    );
  };

  /** Renders a single cell value */
  const renderCell = (row, col) => {
    const value = row[col.key];
    // UTC timestamp columns (e.g. create_time) → campus-local "MM/DD/YYYY h:mmA".
    if (col.type === 'datetime-utc') {
      const formatted = value ? convertIsoUTCToLocal(value, tzOffset) : '';
      return formatted
        ? <span className="dp-cell-text">{formatted}</span>
        : <span className="dp-cell-text--muted">&mdash;</span>;
    }
    if (value != null && value !== '') {
      return <span className="dp-cell-text">{value}</span>;
    }
    return <span className="dp-cell-text--muted">&mdash;</span>;
  };

  return (
    <div className="dp-table-wrap">
      <table className="dp-table">
        <thead className="dp-thead">
          <tr>
            {/* Review action column */}
            <th scope="col" className="dp-th dp-th--actions">
              <span className="dp-sr-only">Review</span>
            </th>

            {/* Data columns */}
            {columnDefs.map((col) => (
              <th key={col.key} scope="col" className="dp-th">
                <div className="dp-th__content">
                  {col.sortable ? (
                    <button
                      type="button"
                      onClick={() => onSort(col.key)}
                      className="dp-th__sort-btn"
                      aria-label={`Sort by ${col.label}`}
                    >
                      {col.label}
                      {renderSortIcon(col.key)}
                    </button>
                  ) : (
                    <span>{col.label}</span>
                  )}

                  {col.filterable ? (
                    <FilterPopover
                      columnKey={col.key}
                      label={col.label}
                      values={getFilterValues(col.key)}
                      selectedValues={filters[col.key] || []}
                      onChange={onFilterChange}
                      data={allData}
                    />
                  ) : null}
                </div>
              </th>
            ))}
          </tr>
        </thead>

        <tbody className="dp-tbody">
          {data.map((row, idx) => {
            const isPendingMoveIn = row.status === 15;
            const moveInDateStr = row.physical_move_in_date
              ? convertIsoToLocalDate(row.physical_move_in_date)
              : 'their move-in date';

            return (
              <React.Fragment key={row.cloud_id || idx}>
                <tr
                  className={`dp-tr ${isPendingMoveIn ? 'ehr-task-row--pending' : 'ehr-task-row'}`}
                  /* Guided-help anchor. Deliberately unset on pre-scheduled
                     move-ins: those rows are not clickable, so pointing the
                     spotlight at one would tell the user to do something that
                     cannot work. Several approvable rows share the id and that is
                     fine — the engine spotlights the first viewport-visible match. */
                  data-help-id={isPendingMoveIn ? undefined : 'ehr.task-row'}
                  onClick={isPendingMoveIn ? undefined : () => onRowClick(row)}
                >
                  {/* Review icon or lock icon */}
                  <td className="dp-td dp-td--actions">
                    {isPendingMoveIn ? (
                      <svg
                        className="dp-icon-md"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        aria-hidden="true"
                        style={{ color: 'var(--color-warning, #f59e0b)' }}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z"
                        />
                      </svg>
                    ) : (
                      <svg
                        className="dp-icon-md"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        aria-hidden="true"
                        style={{ color: 'var(--color-text-tertiary)' }}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    )}
                  </td>

                  {/* Data cells */}
                  {columnDefs.map((col) => (
                    <td key={col.key} className="dp-td">
                      {renderCell(row, col)}
                    </td>
                  ))}
                </tr>

                {/* Pending move-in info banner row */}
                {isPendingMoveIn ? (
                  <tr className="ehr-pending-banner-row">
                    <td colSpan={columnDefs.length + 1} className="ehr-pending-banner-cell">
                      <div className="ehr-pending-banner">
                        <svg className="dp-icon-sm ehr-pending-banner__icon" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span>
                          Scheduled move-in — this task cannot be approved until the resident has been officially allocated their area on their move-in date (<strong>{moveInDateStr}</strong>).
                        </span>
                      </div>
                    </td>
                  </tr>
                ) : null}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default EhrTaskTable;
